import { useState } from 'react';
import { Form } from 'foundations-components/transitional/components/Form';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { convertToTimestamp } from '../../utils/dueDate';
import { ApprovalModalBody } from './ApprovalModalBody';
import ApprovalModalFooter from './ApprovalModalFooter';
import ApprovalModalHeader from './ApprovalModalHeader';
import { ApprovalOperation, ApprovalRules, ApprovalStatus, ApprovalTypes } from '../../types';
import * as trackingConstants from '../../constants/trackingConstants';
import { getErrorSubCategoryFromError } from '../../utils/errorUtils';
import { GATES } from '../../constants/auth';
import { operationSuccessAlert } from '../../utils/alertUtils';
import { shouldPrimaryButtonBeDisabled } from '../../utils/approvalModalUtils';
import { trackInteraction } from '../../utils/usageTracker';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import useApprovalConditions from '../../hooks/useApprovalConditions';
import withErrorBoundary from '../../hoc/withErrorBoundary';
import { useGetIsUngated } from '../../hooks/useGetIsUngated';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getInteractionName(interaction, approvalOperation) {
  return `${interaction}-${approvalOperation}`;
}
function getModalUse(approvalOperation, approvalType) {
  if (approvalOperation === ApprovalOperation.REJECT && approvalType === ApprovalTypes.DEAL) {
    return 'danger';
  }
  return undefined;
}
function ApprovalModal(props) {
  const {
    onClose,
    alertContent,
    showFeedbackSurvey
  } = props;
  const approval = useApprovalContext();
  const {
    approvalType,
    assetName,
    isLoading,
    currentOperation
  } = approval.meta;
  const {
    userCanSkipThisApproval
  } = useApprovalConditions();
  const getHasGate = useGetIsUngated();
  const hasApprovalDueDateGate = getHasGate(GATES.APPROVAL_DUE_DATE);
  const [requestLoading, setRequestLoading] = useState(false);
  const [note, setNote] = useState('');
  const [approverOwnerIds, setApproverOwnerIds] = useState([]);
  const [approvalRule, setApprovalRule] = useState(ApprovalRules.NON_SEQUENTIAL);
  const [errorSubCategory, setErrorSubCategory] = useState(null);
  const [dueDate, setDueDate] = useState(undefined);
  const [dueTime, setDueTime] = useState(undefined);
  const [isDueDateValid, setIsDueDateValid] = useState(true);
  if (currentOperation === null) {
    return null;
  }
  const modalUse = getModalUse(currentOperation, approvalType);
  const trackModalInteraction = (interaction, extra) => {
    const interactionName = getInteractionName(interaction, currentOperation);
    trackInteraction(interactionName, approvalType, extra);
  };
  const onChangeNote = evt => setNote(evt.target.value);
  const onChangeApprovers = value => {
    trackModalInteraction(trackingConstants.APPROVAL_MODAL_CHANGED_APPROVER);
    setApproverOwnerIds(value);
  };
  const onChangeApprovalRule = rule => {
    setApprovalRule(rule);
  };
  const onCancel = () => {
    trackModalInteraction(trackingConstants.APPROVAL_MODAL_CLOSED_WITHOUT_SUBMISSION);
    onClose();
  };
  const onOpenStart = () => {
    setApproverOwnerIds([]);
    setRequestLoading(false);
    trackModalInteraction(trackingConstants.APPROVAL_MODAL_OPENED);
  };
  const getFormSubmissionTrackingExtra = data => {
    const extra = {};
    if (data && 'createdApprovals' in data) {
      extra.approvalId = data.createdApprovals.map(createdApproval => createdApproval.id);
    } else {
      extra.approvalId = data.id;
    }
    if (currentOperation === ApprovalOperation.REQUEST) {
      extra.count = approverOwnerIds.length;
      extra.isSkipped = userCanSkipThisApproval;
    }
    if (currentOperation === ApprovalOperation.APPROVE && data && 'approvalStatus' in data && data.approvalStatus === ApprovalStatus.APPROVED) {
      extra.complete = true;
    }
    if (approvalRule === ApprovalRules.ANY_CAN_APPROVE) {
      extra.rules = trackingConstants.APPROVAL_RULES_ANY_APPROVER;
    } else if (approvalRule === ApprovalRules.NON_SEQUENTIAL) {
      extra.rules = trackingConstants.APPROVAL_RULES_ALL_APPROVERS;
    }
    return extra;
  };
  const onRequestSuccess = data => {
    const extra = getFormSubmissionTrackingExtra(data);
    trackModalInteraction(trackingConstants.APPROVAL_MODAL_FORM_SUBMITTED, extra);
    setErrorSubCategory(null);
    operationSuccessAlert(approvalType, currentOperation, assetName);
    onClose();
    setRequestLoading(false);
    if (props.onRequestSuccess) {
      props.onRequestSuccess(currentOperation);
    }
  };
  const onRequestError = errorResponse => {
    setRequestLoading(false);
    setErrorSubCategory(getErrorSubCategoryFromError(errorResponse));
  };
  const onSubmitForm = evt => {
    evt.preventDefault();
    setRequestLoading(true);
    if (currentOperation === ApprovalOperation.REQUEST) {
      let dueDateTimestamp = undefined;
      if (hasApprovalDueDateGate && dueDate) {
        dueDateTimestamp = convertToTimestamp(dueDate, dueTime);
      }
      approval.api.create(note, approverOwnerIds, approvalRule, dueDateTimestamp).then(onRequestSuccess).catch(onRequestError);
    }
    if (currentOperation === ApprovalOperation.RECALL) {
      approval.api.recall(note).then(onRequestSuccess).catch(onRequestError);
    }
    if (currentOperation === ApprovalOperation.APPROVE) {
      approval.api.approve(note).then(onRequestSuccess).catch(onRequestError);
    }
    if (currentOperation === ApprovalOperation.REJECT) {
      approval.api.reject(note).then(onRequestSuccess).catch(onRequestError);
    }
  };
  const disablePrimaryButton = shouldPrimaryButtonBeDisabled(currentOperation, errorSubCategory, note, approverOwnerIds.length) || !isDueDateValid;
  return /*#__PURE__*/_jsxs(Modal, {
    role: "dialog",
    "data-test-id": `product-approvals-approval-modal-${currentOperation}`,
    width: 600,
    onOpenStart: onOpenStart,
    use: modalUse,
    children: [approval && isLoading && /*#__PURE__*/_jsx(LoadingOverlay, {
      contextual: true
    }), /*#__PURE__*/_jsxs(Form, {
      onSubmit: onSubmitForm,
      children: [/*#__PURE__*/_jsx(ApprovalModalHeader, {
        approvalType: approvalType,
        approvalOperation: currentOperation,
        onCancel: onCancel
      }), /*#__PURE__*/_jsx(ApprovalModalBody, {
        alertContent: alertContent,
        currentOperation: currentOperation,
        approverOwnerIds: approverOwnerIds,
        errorSubCategory: errorSubCategory,
        isLoading: requestLoading,
        note: note,
        onChangeApprovers: onChangeApprovers,
        onChangeApprovalRule: onChangeApprovalRule,
        onChangeNote: onChangeNote,
        dueDate: dueDate,
        setDueDate: setDueDate,
        dueTime: dueTime,
        setDueTime: setDueTime,
        isDueDateValid: isDueDateValid,
        setIsDueDateValid: setIsDueDateValid
      }), /*#__PURE__*/_jsx(ApprovalModalFooter, {
        approvalType: approvalType,
        approvalOperation: currentOperation,
        disablePrimaryButton: disablePrimaryButton,
        onCancel: onCancel,
        isLoading: requestLoading,
        showFeedbackSurvey: showFeedbackSurvey,
        buttonUse: modalUse
      })]
    })]
  });
}
export default withErrorBoundary(ApprovalModal, 'ApprovalModal');